import { authService } from './auth';

export interface Invitation {
  id: string;
  email: string;
  role: string;
  organization: {
    id: number;
    name: string;
    slug: string;
  };
  inviter: {
    id: string;
    name: string;
    email: string;
  };
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expires_at: string;
  accepted_at?: string;
  created_at: string;
}

export interface InvitationAnalytics {
  total_sent: number;
  pending: number;
  accepted: number;
  expired: number;
  revoked: number;
  acceptance_rate: number;
  avg_time_to_accept_hours: number;
  quota_used: number;
  quota_remaining: number;
}

export interface InvitationCreateData {
  email: string;
  role: 'viewer' | 'analyst' | 'manager';
  expires_in_days?: number;
  organization_id?: number;
}

export interface BulkInvitationResult {
  success: boolean;
  sent: number;
  failed: number;
  invitations: Invitation[];
  errors: Array<{ email: string; error: string }>;
}

class InvitationService {
  private baseUrl = '/api/invitations';

  async sendInvitation(data: InvitationCreateData): Promise<{ success: boolean; invitation: Invitation; message: string }> {
    const token = await authService.getValidToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/send`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to send invitation');
    }

    return response.json();
  }

  async sendBulkInvitations(invitations: InvitationCreateData[]): Promise<BulkInvitationResult> {
    const token = await authService.getValidToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/send-bulk`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ invitations }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to send invitations');
    }

    return response.json();
  }

  async inviteOrganization(managerEmail: string, organizationName: string, expiresInDays = 30): Promise<{ success: boolean; invitation: Invitation; message: string }> {
    const token = await authService.getValidToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/organization`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        manager_email: managerEmail,
        organization_name: organizationName,
        expires_in_days: expiresInDays,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to invite organization');
    }

    return response.json();
  }

  async getSentInvitations(organizationId?: number): Promise<Invitation[]> {
    const token = await authService.getValidToken();
    if (!token) throw new Error('Not authenticated');

    const cacheBuster = Date.now();
    const params = organizationId ? `?organization_id=${organizationId}&_cb=${cacheBuster}` : `?_cb=${cacheBuster}`;
    const response = await fetch(`${this.baseUrl}/sent${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch sent invitations');
    }

    const data = await response.json();
    return data.invitations;
  }

  async getOrganizationInvitations(organizationId: number): Promise<Invitation[]> {
    const token = await authService.getValidToken();
    if (!token) throw new Error('Not authenticated');

    // Add cache busting parameter
    const cacheBuster = Date.now();
    const response = await fetch(`${this.baseUrl}/organization/${organizationId}?_cb=${cacheBuster}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch organization invitations');
    }

    const data = await response.json();
    return data.invitations;
  }

  async getReceivedInvitations(email?: string): Promise<Invitation[]> {
    const token = await authService.getValidToken();
    const params = email ? `?email=${encodeURIComponent(email)}` : '';
    
    const response = await fetch(`${this.baseUrl}/received${params}`, {
      headers: token ? {
        'Authorization': `Bearer ${token}`,
      } : {},
    });

    if (!response.ok) {
      throw new Error('Failed to fetch received invitations');
    }

    const data = await response.json();
    return data.invitations;
  }

  async acceptInvitation(token: string): Promise<{ success: boolean; invitation: Invitation; requires_registration: boolean; message: string }> {
    const authToken = await authService.getValidToken();
    
    const response = await fetch(`${this.baseUrl}/accept`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to accept invitation');
    }

    return response.json();
  }

  async resendInvitation(invitationId: string): Promise<{ success: boolean; message: string }> {
    const token = await authService.getValidToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/${invitationId}/resend`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to resend invitation');
    }

    return response.json();
  }

  async revokeInvitation(invitationId: string): Promise<{ success: boolean; message: string }> {
    const token = await authService.getValidToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/${invitationId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to revoke invitation');
    }

    return response.json();
  }

  async getInvitationAnalytics(organizationId: number): Promise<InvitationAnalytics> {
    const token = await authService.getValidToken();
    if (!token) throw new Error('Not authenticated');

    const cacheBuster = Date.now();
    const response = await fetch(`${this.baseUrl}/analytics/${organizationId}?_cb=${cacheBuster}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch invitation analytics');
    }

    return response.json();
  }

  async checkInvitation(token: string): Promise<{ valid: boolean; reason?: string; invitation: Invitation }> {
    const response = await fetch(`${this.baseUrl}/check/${token}`);

    if (!response.ok) {
      throw new Error('Failed to check invitation');
    }

    return response.json();
  }

  async getInvitationDetails(token: string): Promise<any> {
    const response = await fetch(`${this.baseUrl}/details/${token}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get invitation details');
    }

    return response.json();
  }

  parseBulkEmails(input: string): InvitationCreateData[] {
    const lines = input.split('\n').filter(line => line.trim());
    const invitations: InvitationCreateData[] = [];
    
    for (const line of lines) {
      // Support formats:
      // email@example.com
      // email@example.com, analyst
      // email@example.com, viewer, 14
      const parts = line.split(',').map(p => p.trim());
      
      if (parts[0] && this.isValidEmail(parts[0])) {
        invitations.push({
          email: parts[0],
          role: (parts[1] as any) || 'analyst',
          expires_in_days: parts[2] ? parseInt(parts[2]) : undefined,
        });
      }
    }
    
    return invitations;
  }

  private isValidEmail(email: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }
}

export const invitationService = new InvitationService();